import { absoluteValue } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import { createEvmActionSource, isHex } from '@pkg/evm'
import { createSolanaActionSource } from '@pkg/solana'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import {
  backpackAccountCategory,
  networkEvmChainIds,
  solanaNetworks,
} from '../constants'
import { createClient } from '../createClient'
import { depositsResponseSchema, withdrawalsResponseSchema } from './schema'

/**
 * Backpack の `createdAt` は naive ISO date-time 文字列 (例: `2024-01-15T12:34:56.789`) のため、
 * UTC として解釈する必要がある。タイムゾーン指定がない場合は `Z` を付与してから parse する。
 * @param createdAt - レスポンスの createdAt 値
 * @returns UNIX milliseconds
 */
const parseCreatedAt = (createdAt: string): number => {
  const hasTimezone = /([Zz]|[+-]\d{2}:?\d{2})$/.test(createdAt)
  const isoString = hasTimezone ? createdAt : `${createdAt}Z`
  const ms = Date.parse(isoString)
  if (Number.isNaN(ms)) {
    throw new Error(`Invalid Backpack createdAt value: ${createdAt}`)
  }
  return ms
}

/**
 * Backpack の入出金履歴 (`/wapi/v1/capital/deposits` + `/wapi/v1/capital/withdrawals`) を取得し、
 * 手数料を Action 化しつつ、EVM / Solana トランザクションについて検出可能な source を抽出する。
 */
export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  createHeaders,
  from,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const query: Record<string, string> = {
    limit: config.limit.toString(),
  }
  if (from) {
    query.from = from.toString()
  }
  if (to) {
    query.to = to.toString()
  }

  const [deposits, withdrawals] = await Promise.all([
    client.run({
      method: 'GET',
      path: '/wapi/v1/capital/deposits',
      query,
      responseSchema: depositsResponseSchema,
    }),
    client.run({
      method: 'GET',
      path: '/wapi/v1/capital/withdrawals',
      query,
      responseSchema: withdrawalsResponseSchema,
    }),
  ])

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  // 入金: status === 'confirmed' のみ extract source。手数料はないため Action 不要。
  for (const item of deposits) {
    if (item.status !== 'confirmed') {
      continue
    }
    const timestamp = parseCreatedAt(item.createdAt)
    fetchedFrom =
      fetchedFrom === undefined ? timestamp : Math.min(fetchedFrom, timestamp)
    fetchedTo =
      fetchedTo === undefined ? timestamp : Math.max(fetchedTo, timestamp)

    if (item.transactionHash) {
      const evmChainId = networkEvmChainIds[item.source]
      if (evmChainId && isHex(item.transactionHash)) {
        sources.push(
          createEvmActionSource({
            chainId: evmChainId,
            transactionHash: item.transactionHash,
          }),
        )
      } else if (solanaNetworks.has(item.source)) {
        sources.push(
          createSolanaActionSource({ signature: item.transactionHash }),
        )
      }
    }

    sources.push(`${backpackAccountCategory}_${config.type}_deposit_${item.id}`)
  }

  // 出金: 手数料を fee Action として記録し、検出可能なオンチェーン source を抽出。
  for (const item of withdrawals) {
    if (item.status !== 'confirmed') {
      continue
    }
    const timestamp = parseCreatedAt(item.createdAt)
    fetchedFrom =
      fetchedFrom === undefined ? timestamp : Math.min(fetchedFrom, timestamp)
    fetchedTo =
      fetchedTo === undefined ? timestamp : Math.max(fetchedTo, timestamp)

    if (item.transactionHash) {
      const evmChainId = networkEvmChainIds[item.blockchain]
      if (evmChainId && isHex(item.transactionHash)) {
        sources.push(
          createEvmActionSource({
            chainId: evmChainId,
            transactionHash: item.transactionHash,
          }),
        )
      } else if (solanaNetworks.has(item.blockchain)) {
        sources.push(
          createSolanaActionSource({ signature: item.transactionHash }),
        )
      }
    }

    sources.push(
      `${backpackAccountCategory}_${config.type}_withdrawal_${item.id}`,
    )

    if (item.fee.value !== 0n) {
      const feeAsset = getAsset(item.symbol)
      if (!feeAsset) {
        throw new AssetNotFoundException(item.symbol)
      }
      actions.push({
        comment: `Withdrawal Fee of ${item.symbol}`,
        evidence: 'system-rule',
        order: 0,
        source: `${backpackAccountCategory}_${config.type}_withdrawal_fee_${item.id}`,
        type: 'fee',
        timestamp,
        transfers: [
          {
            asset: feeAsset,
            amount: absoluteValue(item.fee),
            direction: 'out',
          },
        ],
      })
    }
  }

  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions,
    count: deposits.length + withdrawals.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
